import { useState, useCallback, useMemo } from 'react';
import {
  UploadIcon,
  FileXlsIcon,
  WarningCircleIcon,
  CheckCircleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import {
  AlertContainer,
  AlertDescription,
  AlertTitle,
} from 'src/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { Badge } from 'src/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  useParseReportedFile,
  useGetDivisionsForMapping,
} from 'src/api/rpc/hooks/rebates/useHistoricalReportedImport';
import type {
  ParseReportedFileResponse,
  SupplierSuggestion,
  DivisionSuggestion,
} from '@erp/rpc';
import { cn } from 'src/lib/utils';

// ============================================================================
// Types
// ============================================================================

export interface SupplierMappingEntry {
  supplierReference: string;
  supplierId: string;
  agreementId: string;
}

export interface DivisionMappingEntry {
  divisionReference: string;
  divisionId: string;
}

interface ReportedStep1UploadProps {
  onComplete: (
    parseResult: ParseReportedFileResponse,
    xlsxBase64: string,
    fileName: string,
    targetMonth: string,
    supplierMappings: SupplierMappingEntry[],
    divisionMappings: DivisionMappingEntry[],
  ) => void;
  onBack: () => void;
  onError: (error: string) => void;
  isLoading: boolean;
  error?: string;
}

interface FileState {
  file: File | null;
  base64: string;
  fileName: string;
}

// ============================================================================
// Component
// ============================================================================

// Generate month options for 2025 only
function getMonthOptions(): { value: string; label: string }[] {
  const options: { value: string; label: string }[] = [];
  const year = 2025;

  // Generate all 12 months for 2025
  for (let month = 1; month <= 12; month++) {
    const value = `${year}-${String(month).padStart(2, '0')}`;
    const label = new Date(year, month - 1).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    });
    options.push({ value, label });
  }

  return options;
}

export default function ReportedStep1Upload({
  onComplete,
  onBack,
  onError,
  error,
}: ReportedStep1UploadProps) {
  const [xlsxFile, setXlsxFile] = useState<FileState>({
    file: null,
    base64: '',
    fileName: '',
  });
  const [targetMonth, setTargetMonth] = useState<string>('');
  const [parseResult, setParseResult] =
    useState<ParseReportedFileResponse | null>(null);

  const monthOptions = getMonthOptions();

  // Track user overrides for suppliers: supplierReference -> { supplierId, agreementId }
  const [supplierOverrides, setSupplierOverrides] = useState<
    Record<string, { supplierId: string; agreementId: string }>
  >({});

  // Track user overrides for divisions: divisionReference -> divisionId
  const [divisionOverrides, setDivisionOverrides] = useState<
    Record<string, string>
  >({});

  const parseFileMutation = useParseReportedFile();
  const { data: divisionsData } = useGetDivisionsForMapping();

  const allDivisions = divisionsData?.divisions ?? [];

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
    });
  };

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      try {
        const base64 = await fileToBase64(file);
        setXlsxFile({ file, base64, fileName: file.name });
        setParseResult(null);
        setSupplierOverrides({});
        setDivisionOverrides({});
      } catch (err) {
        onError('Failed to read xlsx file');
      }
    },
    [onError],
  );

  const handleClearFile = () => {
    setXlsxFile({ file: null, base64: '', fileName: '' });
    setParseResult(null);
    setSupplierOverrides({});
    setDivisionOverrides({});
  };

  const handleMonthChange = (value: string) => {
    setTargetMonth(value);
    setParseResult(null);
    setSupplierOverrides({});
    setDivisionOverrides({});
  };

  const handleParseFile = async () => {
    if (!xlsxFile.base64) {
      onError('Please upload an xlsx file');
      return;
    }
    if (!targetMonth) {
      onError('Please select a target month');
      return;
    }

    try {
      const result = await parseFileMutation.mutateAsync({
        xlsxBase64: xlsxFile.base64,
        targetMonth,
      });
      setParseResult(result);

      // Initialize supplier overrides from suggestions
      const initialSupplierOverrides: Record<
        string,
        { supplierId: string; agreementId: string }
      > = {};
      for (const suggestion of result.supplierSuggestions) {
        if (suggestion.suggestedSupplierId && suggestion.suggestedAgreementId) {
          initialSupplierOverrides[suggestion.supplierReference] = {
            supplierId: suggestion.suggestedSupplierId,
            agreementId: suggestion.suggestedAgreementId,
          };
        }
      }
      setSupplierOverrides(initialSupplierOverrides);

      // Initialize division overrides from suggestions
      const initialDivisionOverrides: Record<string, string> = {};
      for (const suggestion of result.divisionSuggestions) {
        if (suggestion.suggestedDivisionId) {
          initialDivisionOverrides[suggestion.divisionReference] =
            suggestion.suggestedDivisionId;
        }
      }
      setDivisionOverrides(initialDivisionOverrides);
    } catch (err) {
      // Error is handled by the mutation hook
    }
  };

  const handleSupplierChange = (
    supplierReference: string,
    supplierId: string,
    agreementId: string,
  ) => {
    setSupplierOverrides((prev) => ({
      ...prev,
      [supplierReference]: { supplierId, agreementId },
    }));
  };

  const handleDivisionChange = (
    divisionReference: string,
    divisionId: string,
  ) => {
    setDivisionOverrides((prev) => ({
      ...prev,
      [divisionReference]: divisionId,
    }));
  };

  // Build final mappings
  const supplierMappings = useMemo((): SupplierMappingEntry[] => {
    if (!parseResult) return [];
    return parseResult.supplierSuggestions
      .filter((s) => supplierOverrides[s.supplierReference])
      .map((s) => ({
        supplierReference: s.supplierReference,
        supplierId: supplierOverrides[s.supplierReference].supplierId,
        agreementId: supplierOverrides[s.supplierReference].agreementId,
      }));
  }, [parseResult, supplierOverrides]);

  const divisionMappings = useMemo((): DivisionMappingEntry[] => {
    if (!parseResult) return [];
    return parseResult.divisionSuggestions
      .filter((d) => divisionOverrides[d.divisionReference])
      .map((d) => ({
        divisionReference: d.divisionReference,
        divisionId: divisionOverrides[d.divisionReference],
      }));
  }, [parseResult, divisionOverrides]);

  // Check if all items are mapped
  const allSuppliersMapped = useMemo(() => {
    if (!parseResult) return false;
    return parseResult.supplierSuggestions.every(
      (s) => supplierOverrides[s.supplierReference]?.agreementId,
    );
  }, [parseResult, supplierOverrides]);

  const allDivisionsMapped = useMemo(() => {
    if (!parseResult) return false;
    return parseResult.divisionSuggestions.every(
      (d) => divisionOverrides[d.divisionReference],
    );
  }, [parseResult, divisionOverrides]);

  const handleContinue = () => {
    if (
      !parseResult ||
      !allSuppliersMapped ||
      !allDivisionsMapped ||
      !targetMonth
    )
      return;
    onComplete(
      parseResult,
      xlsxFile.base64,
      xlsxFile.fileName,
      targetMonth,
      supplierMappings,
      divisionMappings,
    );
  };

  const canParse = xlsxFile.base64 && targetMonth;
  const canContinue = parseResult && allSuppliersMapped && allDivisionsMapped;

  // Check for blocking errors
  const hasBlockingErrors = parseResult?.validationIssues.some(
    (i) => i.severity === 'error',
  );

  const getCategoryBadge = (category: string) => {
    switch (category) {
      case 'conditional':
        return (
          <Badge variant="outline" className="bg-blue-500/10 text-blue-600">
            Conditional
          </Badge>
        );
      case 'non_conditional':
        return (
          <Badge variant="outline" className="bg-green-500/10 text-green-600">
            Non-Conditional
          </Badge>
        );
      case 'redistribution':
        return (
          <Badge variant="outline" className="bg-purple-500/10 text-purple-600">
            Redistribution
          </Badge>
        );
      default:
        return <Badge variant="outline">{category}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Upload & Map Data</h2>
        <p className="text-sm text-muted-foreground">
          Upload the Enable Reported Data xlsx file and confirm
          supplier/division mappings.
        </p>
      </div>

      {error && (
        <AlertContainer variant="destructive">
          <WarningCircleIcon className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </AlertContainer>
      )}

      {/* Target Month Selection */}
      <div className="max-w-md">
        <label className="text-sm font-medium">Target Month</label>
        <p className="text-xs text-muted-foreground mb-2">
          Select the month this file represents (required)
        </p>
        <Select value={targetMonth} onValueChange={handleMonthChange}>
          <SelectTrigger className={cn(!targetMonth && 'border-orange-400')}>
            <SelectValue placeholder="Select month..." />
          </SelectTrigger>
          <SelectContent>
            {monthOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* File Upload Section */}
      <div className="max-w-md">
        <label className="text-sm font-medium">Reported Data XLSX</label>
        {!xlsxFile.file ? (
          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-muted/30 hover:bg-muted/50 transition-colors mt-2">
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <UploadIcon className="w-8 h-8 mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Click to upload xlsx file
              </p>
            </div>
            <input
              type="file"
              className="hidden"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
            />
          </label>
        ) : (
          <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30 mt-2">
            <div className="flex items-center gap-2">
              <FileXlsIcon className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium truncate max-w-[300px]">
                {xlsxFile.fileName}
              </span>
            </div>
            <Button variant="ghost" size="sm" onClick={handleClearFile}>
              <XIcon className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Parse Button */}
      {canParse && !parseResult && (
        <div className="flex justify-start">
          <Button
            onClick={handleParseFile}
            disabled={parseFileMutation.isPending}
          >
            {parseFileMutation.isPending ? 'Parsing...' : 'Parse File'}
          </Button>
        </div>
      )}

      {/* Parse Results */}
      {parseResult && (
        <div className="space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-muted/30 rounded-lg text-center">
              <p className="text-2xl font-bold">{parseResult.totalRows}</p>
              <p className="text-sm text-muted-foreground">Total Rows</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg text-center">
              <p className="text-2xl font-bold">
                {parseResult.aggregatedRowCount}
              </p>
              <p className="text-sm text-muted-foreground">Aggregated</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg text-center">
              <p className="text-2xl font-bold">
                {parseResult.uniqueSuppliers}
              </p>
              <p className="text-sm text-muted-foreground">Suppliers</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg text-center">
              <p className="text-2xl font-bold">
                ${parseResult.totalAmount.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground">Total Amount</p>
            </div>
          </div>

          {/* Validation Issues */}
          {parseResult.validationIssues.length > 0 && (
            <AlertContainer
              variant={hasBlockingErrors ? 'destructive' : 'default'}
            >
              <WarningCircleIcon className="h-4 w-4" />
              <AlertTitle>
                {hasBlockingErrors ? 'Validation Errors' : 'Warnings'}
              </AlertTitle>
              <AlertDescription>
                <ul className="list-disc pl-4 mt-2 space-y-1">
                  {parseResult.validationIssues.slice(0, 5).map((issue, i) => (
                    <li key={i} className="text-sm">
                      {issue.message}
                    </li>
                  ))}
                  {parseResult.validationIssues.length > 5 && (
                    <li className="text-sm text-muted-foreground">
                      ...and {parseResult.validationIssues.length - 5} more
                    </li>
                  )}
                </ul>
              </AlertDescription>
            </AlertContainer>
          )}

          {/* Mapping Status */}
          {allSuppliersMapped && allDivisionsMapped ? (
            <AlertContainer className="border-green-500/50 bg-green-500/10">
              <CheckCircleIcon className="h-4 w-4 text-green-500" />
              <AlertTitle>All Mappings Complete</AlertTitle>
              <AlertDescription>
                All suppliers and divisions have been mapped. Review and
                continue.
              </AlertDescription>
            </AlertContainer>
          ) : (
            <AlertContainer variant="destructive">
              <WarningCircleIcon className="h-4 w-4" />
              <AlertTitle>Missing Mappings</AlertTitle>
              <AlertDescription>
                Some suppliers or divisions need to be mapped before you can
                continue.
              </AlertDescription>
            </AlertContainer>
          )}

          {/* Supplier Mapping Table */}
          <div>
            <h3 className="text-sm font-medium mb-2">
              Supplier Mapping ({parseResult.uniqueSuppliers})
            </h3>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Supplier Reference</TableHead>
                    <TableHead>Supplier Name</TableHead>
                    <TableHead>Matched Agreement</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Rows</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parseResult.supplierSuggestions.map(
                    (suggestion: SupplierSuggestion, idx: number) => {
                      const override =
                        supplierOverrides[suggestion.supplierReference];
                      const isMapped = Boolean(override?.agreementId);

                      return (
                        <TableRow
                          key={idx}
                          className={cn(!isMapped && 'bg-red-500/5')}
                        >
                          <TableCell className="font-mono text-sm">
                            {suggestion.supplierReference}
                          </TableCell>
                          <TableCell>{suggestion.supplierName}</TableCell>
                          <TableCell className="min-w-[250px]">
                            {suggestion.suggestedAgreementId ? (
                              <div className="text-sm">
                                <span className="font-medium">
                                  {suggestion.suggestedAgreementName}
                                </span>
                              </div>
                            ) : (
                              <span className="text-red-500 text-sm">
                                No agreement found
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            ${suggestion.totalAmount.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">
                            {suggestion.rowCount}
                          </TableCell>
                        </TableRow>
                      );
                    },
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Division Mapping Table */}
          <div>
            <h3 className="text-sm font-medium mb-2">
              Division Mapping ({parseResult.uniqueDivisions})
            </h3>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Division Reference</TableHead>
                    <TableHead>Division Name</TableHead>
                    <TableHead>Mapped To</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Rows</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parseResult.divisionSuggestions.map(
                    (suggestion: DivisionSuggestion, idx: number) => {
                      const selectedDivisionId =
                        divisionOverrides[suggestion.divisionReference];
                      const isMapped = Boolean(selectedDivisionId);

                      return (
                        <TableRow
                          key={idx}
                          className={cn(!isMapped && 'bg-red-500/5')}
                        >
                          <TableCell className="font-mono text-sm">
                            {suggestion.divisionReference}
                          </TableCell>
                          <TableCell>{suggestion.divisionName}</TableCell>
                          <TableCell className="min-w-[250px]">
                            <Select
                              value={selectedDivisionId || ''}
                              onValueChange={(value) =>
                                handleDivisionChange(
                                  suggestion.divisionReference,
                                  value,
                                )
                              }
                            >
                              <SelectTrigger
                                className={cn(
                                  'w-full',
                                  !isMapped && 'border-red-500',
                                )}
                              >
                                <SelectValue placeholder="Select division..." />
                              </SelectTrigger>
                              <SelectContent>
                                {allDivisions.map((division) => (
                                  <SelectItem
                                    key={division.id}
                                    value={division.id}
                                  >
                                    {division.name}
                                    {division.internalCode && (
                                      <span className="text-muted-foreground ml-2">
                                        ({division.internalCode})
                                      </span>
                                    )}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-right">
                            ${suggestion.totalAmount.toLocaleString()}
                          </TableCell>
                          <TableCell className="text-right">
                            {suggestion.rowCount}
                          </TableCell>
                        </TableRow>
                      );
                    },
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* Aggregated Data Preview */}
          <div>
            <h3 className="text-sm font-medium mb-2">
              Aggregated Data Preview (first 20)
            </h3>
            <div className="border rounded-lg overflow-hidden max-h-80 overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Division</TableHead>
                    <TableHead>Month</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Rows</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parseResult.aggregatedRows.slice(0, 20).map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell className="font-mono text-sm">
                        {row.supplierReference}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {row.divisionReference}
                      </TableCell>
                      <TableCell>{row.month}</TableCell>
                      <TableCell>{getCategoryBadge(row.category)}</TableCell>
                      <TableCell className="text-right">
                        ${row.accrualEarnings.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right">
                        {row.rowCount}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between pt-4 border-t">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button
          onClick={handleContinue}
          disabled={!canContinue || hasBlockingErrors}
          className={cn((!canContinue || hasBlockingErrors) && 'opacity-50')}
        >
          Continue to Import
        </Button>
      </div>
    </div>
  );
}
